import React from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, LinearProgress, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import type { EditForClarityCleanupProgress, EditForClarityCleanupStep } from "./ai/editForClarityCleanup";

type EditForClarityCleanupDialogProps = {
    open: boolean;
    progress: EditForClarityCleanupProgress | null;
    stopRequested: boolean;
    onClose: () => void;
    onStop: () => void;
};

const formatSecondsCompact = (value: number): string => {
    const seconds = Number(value);
    if (!Number.isFinite(seconds) || seconds <= 0) return "0s";
    if (seconds < 60) return `${seconds.toFixed(seconds >= 10 ? 0 : 1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${String(secs).padStart(2, "0")}s`;
};

const getStepById = (progress: EditForClarityCleanupProgress, id: EditForClarityCleanupStep["id"]): EditForClarityCleanupStep | undefined =>
    progress.steps.find((step) => step.id === id);

const getPhaseCopy = (
    progress: EditForClarityCleanupProgress,
    stopRequested: boolean,
): { title: string; subtitle: string } => {
    if (progress.phase === "completed") {
        return {
            title: "Cleanup complete",
            subtitle: "Your recording has been cleaned up. You can review and refine below.",
        };
    }
    if (progress.phase === "failed") {
        return {
            title: "Cleanup failed",
            subtitle: progress.errorMessage || "Something went wrong while applying cleanup.",
        };
    }
    if (progress.phase === "stopped") {
        return {
            title: "Cleanup stopped",
            subtitle: "Cleanup was stopped. You can continue editing manually.",
        };
    }
    return {
        title: "Running cleanup",
        subtitle: stopRequested
            ? "Stopping after the current operation..."
            : "Applying Edit for Clarity to your recording.",
    };
};

const getRowTone = (step: EditForClarityCleanupStep): { bg: string; fg: string } => {
    if (step.state === "completed") return { bg: "#ECFDF3", fg: "#027A48" };
    if (step.state === "running") return { bg: "#EFF6FF", fg: "#1D4ED8" };
    if (step.state === "failed") return { bg: "#FEF2F2", fg: "#B91C1C" };
    if (step.state === "stopped") return { bg: "#FFFBEB", fg: "#B45309" };
    if (step.state === "skipped") return { bg: "var(--app-bg-subtle)", fg: "var(--app-text-muted)" };
    return { bg: "var(--app-bg-subtle)", fg: "var(--app-text-muted)" };
};

const SummaryStatCard: React.FC<{ value: string; label: string }> = ({ value, label }) => (
    <Box
        sx={{
            border: "1px solid var(--app-border-subtle)",
            borderRadius: "10px",
            px: "11px",
            py: "10px",
            minHeight: "74px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "3px",
        }}
    >
        <Typography sx={{ fontSize: "24px", lineHeight: 1.15, fontWeight: 700, color: "var(--app-text-primary)" }}>
            {value}
        </Typography>
        <Typography sx={{ fontSize: "12px", lineHeight: 1.35, color: "var(--app-text-muted)", fontWeight: 500 }}>
            {label}
        </Typography>
    </Box>
);

const EditForClarityCleanupDialog: React.FC<EditForClarityCleanupDialogProps> = ({
    open,
    progress,
    stopRequested,
    onClose,
    onStop,
}) => {
    if (!progress) return null;

    const phaseCopy = getPhaseCopy(progress, stopRequested);
    const isRunning = progress.phase === "running";
    const isCompleted = progress.phase === "completed";

    const retakesStep = getStepById(progress, "remove_retakes");
    const retakesOff = retakesStep?.state === "skipped";

    const actionableSteps = progress.steps.filter((step) => step.state !== "skipped");
    const totalSteps = actionableSteps.length;
    const completedSteps = actionableSteps.filter((step) => step.state === "completed").length;
    const stoppedSteps = actionableSteps.filter((step) => step.state === "stopped").length;
    const failedSteps = actionableSteps.filter((step) => step.state === "failed").length;
    const finishedSteps = completedSteps + stoppedSteps + failedSteps;
    const progressPct = totalSteps > 0 ? Math.round((finishedSteps / totalSteps) * 100) : 100;

    const footerPrimaryLabel = isRunning
        ? (stopRequested ? "Stopping..." : "Stop")
        : "Continue editing";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: "min(560px, 92vw)",
                    borderRadius: "16px",
                    border: "1px solid var(--app-border-subtle)",
                    boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
                    backgroundColor: "var(--app-bg-surface)",
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box
                    sx={{
                        px: "18px",
                        py: "14px",
                        borderBottom: "1px solid var(--app-border-subtle)",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "10px",
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", minWidth: 0 }}>
                        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "var(--app-text-primary)", lineHeight: 1.2 }}>
                            {phaseCopy.title}
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: "var(--app-text-muted)", lineHeight: 1.45 }}>
                            {phaseCopy.subtitle}
                        </Typography>
                    </Box>
                    {isCompleted ? (
                        <Box
                            sx={{
                                flexShrink: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                px: "10px",
                                py: "5px",
                                borderRadius: "999px",
                                border: "1px solid #BBF7D0",
                                backgroundColor: "#ECFDF3",
                                color: "#027A48",
                                fontSize: "12px",
                                fontWeight: 600,
                            }}
                        >
                            <CheckCircleRoundedIcon sx={{ fontSize: "15px" }} />
                            Cleanup complete
                        </Box>
                    ) : null}
                </Box>

                <Box sx={{ px: "18px", py: "14px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px" }}>
                        <SummaryStatCard
                            value={String(progress.fillerWordsRemoved)}
                            label="filler words removed"
                        />
                        <SummaryStatCard
                            value={formatSecondsCompact(progress.silenceSecondsRemoved)}
                            label="trimmed"
                        />
                        <SummaryStatCard
                            value={retakesOff ? "Off" : String(progress.retakesDetected)}
                            label={retakesOff ? "retakes" : "retakes marked"}
                        />
                    </Box>

                    {isRunning ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <LinearProgress
                                variant="determinate"
                                value={progressPct}
                                sx={{
                                    height: "8px",
                                    borderRadius: "999px",
                                    backgroundColor: "var(--app-bg-subtle)",
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: "999px",
                                        backgroundColor: "#2563EB",
                                    },
                                }}
                            />
                            <Typography sx={{ fontSize: "12px", color: "var(--app-text-muted)", textAlign: "right" }}>
                                {progressPct}%
                            </Typography>
                        </Box>
                    ) : null}

                    {isCompleted ? (
                        <Box
                            sx={{
                                border: "1px solid var(--app-border-subtle)",
                                borderRadius: "10px",
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    px: "12px",
                                    py: "10px",
                                    borderBottom: "1px solid var(--app-border-subtle)",
                                }}
                            >
                                <Typography sx={{ fontSize: "13px", color: "var(--app-text-primary)", fontWeight: 500 }}>
                                    Retakes
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "var(--app-text-muted)", fontWeight: 600 }}>
                                    {retakesOff ? "Off" : `${progress.retakesDetected} marked`}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    px: "12px",
                                    py: "10px",
                                    borderBottom: "1px solid var(--app-border-subtle)",
                                }}
                            >
                                <Typography sx={{ fontSize: "13px", color: "var(--app-text-primary)", fontWeight: 500 }}>
                                    Filler words
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "var(--app-text-muted)", fontWeight: 600 }}>
                                    {progress.fillerWordsRemoved} removed
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    px: "12px",
                                    py: "10px",
                                }}
                            >
                                <Typography sx={{ fontSize: "13px", color: "var(--app-text-primary)", fontWeight: 500 }}>
                                    Silence
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "var(--app-text-muted)", fontWeight: 600 }}>
                                    {formatSecondsCompact(progress.silenceSecondsRemoved)} trimmed
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                border: "1px solid var(--app-border-subtle)",
                                borderRadius: "10px",
                                overflow: "hidden",
                            }}
                        >
                            {progress.steps.map((step, index) => {
                                const tone = getRowTone(step);
                                return (
                                    <Box
                                        key={step.id}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: "12px",
                                            px: "12px",
                                            py: "10px",
                                            borderTop: index === 0 ? "none" : "1px solid var(--app-border-subtle)",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "13px", color: "var(--app-text-primary)", fontWeight: 500 }}>
                                            {step.label}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                px: "8px",
                                                py: "3px",
                                                borderRadius: "999px",
                                                border: "1px solid var(--app-border-subtle)",
                                                backgroundColor: tone.bg,
                                                color: tone.fg,
                                                fontSize: "11px",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {step.state === "running" ? <CircularProgress size={10} sx={{ color: tone.fg }} /> : null}
                                            {step.state === "skipped" ? "Off" : step.detail}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {isCompleted ? (
                        <Typography sx={{ fontSize: "12px", color: "var(--app-text-muted)", lineHeight: 1.45 }}>
                            You can review or restore any change below.
                        </Typography>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        px: "18px",
                        py: "12px",
                        borderTop: "1px solid var(--app-border-subtle)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: "8px",
                    }}
                >
                    <Button
                        onClick={onClose}
                        sx={{
                            textTransform: "none",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "var(--app-icon-primary)",
                            border: "1px solid var(--app-border-muted)",
                            borderRadius: "8px",
                            px: "14px",
                            py: "6px",
                            '&:hover': {
                                borderColor: "var(--app-text-subtle)",
                                backgroundColor: "var(--app-bg-subtle)",
                            },
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={isRunning ? onStop : onClose}
                        disabled={isRunning ? stopRequested : false}
                        sx={{
                            textTransform: "none",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "var(--app-bg-surface)",
                            border: "1px solid var(--app-text-primary)",
                            borderRadius: "8px",
                            px: "14px",
                            py: "6px",
                            backgroundColor: isRunning ? "#1F2937" : "var(--app-text-primary)",
                            '&:hover': {
                                borderColor: "var(--app-text-primary)",
                                backgroundColor: "#1F2937",
                            },
                            '&.Mui-disabled': {
                                borderColor: "var(--app-border-muted)",
                                backgroundColor: "var(--app-text-subtle)",
                                color: "var(--app-bg-subtle)",
                            },
                        }}
                    >
                        {footerPrimaryLabel}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default EditForClarityCleanupDialog;
